import _ from "lodash";
import { ELLIPSE_ELEMENT_CONFIG } from "../KonvaConfig";
import { KONVA_ATTR, KONVA_EVENTS } from "../KonvaConstants";
import {
  generateElementName,
  getCanvasRelativeSize,
  listenBackEvents,
  listenDocumentEvent,
} from "../../KonvaCommonUtils";
import { generateRandomId } from "Utils";

/**
 *
 * @param {*} item
 * @param {*} konva
 * @returns New shape instance
 */
export const createEllipseNode = (
  item,
  { mode, questionType, layer, transformer, konva } = {}
) => {
  const strokeWidth = getCanvasRelativeSize(
    ELLIPSE_ELEMENT_CONFIG.STROKE_WIDTH
  );
  const props = {
    id: _.get(item, "id"),
    x: _.get(item, "x"),
    y: _.get(item, "y"),
    name: generateElementName(item),
    draggable: _.get(item, "draggable", false),
    dash: [strokeWidth, strokeWidth],
    strokeWidth: strokeWidth,
    stroke: ELLIPSE_ELEMENT_CONFIG.STROKE_COLOR,
    radiusX: _.get(item, "radiusX", ELLIPSE_ELEMENT_CONFIG.DEFAULT_RADIUS),
    radiusY: _.get(item, "radiusY", ELLIPSE_ELEMENT_CONFIG.DEFAULT_RADIUS),
  };

  if (mode === "create") {
    props.fillEnabled = false;
    props.hitStrokeWidth = Math.min(strokeWidth * 9, 20);
  }

  const dash = {
    ...props,
    name: props.id + "-dash-support",
    id: props.id + "-dash-support",
    stroke: "white",
    draggable: false,
    listening: false,
  };
  delete dash.dash;
  delete dash.hitFunc;

  const supportDashLine = new konva.Ellipse(dash);
  layer.add(supportDashLine);

  const node = new konva.Ellipse(props);
  layer.add(node);

  const updateSupportDashLine = () => {
    supportDashLine.setAttrs(node.attrs);
    supportDashLine.dash([10, 0]);
    supportDashLine.stroke("white");
  };

  if (transformer) {
    node.on(KONVA_EVENTS.DRAGMOVE, updateSupportDashLine);
    transformer.on(KONVA_EVENTS.TRANSFORM, updateSupportDashLine);
  }

  if (questionType?.isDragAndDrop) {
    // * adding correct element as attributes
    node.setAttr(
      KONVA_ATTR.DROPZONE_TAGGED_ANSWER,
      _.get(item, "correctElements", [])
    );
  }

  return node;
};

/**
 *
 * @param {*} props
 * @description draw ellipse
 */
export const drawEllipseEventListener = props => {
  const { konva, stage, layer, transformer } = props;
  stage.container().style.cursor = "crosshair";

  let ellipse, startPos, isShiftPressed;

  /**
   * Cancelling drawing mode and events
   */
  const cancelDrawing = e => {
    if (!stage.container().contains(e.target)) {
      stage.setAttr(KONVA_ATTR.IS_DRAWING_POLYGON, false);
      stage.container().style.cursor = "default";

      if (_.isFunction(props?.onDrawCancel)) props.onDrawCancel();
      if (_.isFunction(cleanClickEvent)) cleanClickEvent();
      if (_.isFunction(cleanKeyUpEvent)) cleanKeyUpEvent();
      if (_.isFunction(cleanKeyDownEvent)) cleanKeyDownEvent();
    }
  };

  const listenForShift = e => {
    isShiftPressed = _.get(e, "key") === "Shift" ? true : isShiftPressed;
  };
  const unlistenFoShift = e => {
    isShiftPressed = _.get(e, "key") === "Shift" ? false : isShiftPressed;
  };

  const cleanClickEvent = listenDocumentEvent("click", cancelDrawing);
  const cleanKeyUpEvent = listenDocumentEvent("keyup", unlistenFoShift);
  const cleanKeyDownEvent = listenDocumentEvent("keydown", listenForShift);

  /**
   * starting drawing here on mouse down
   */
  const onMouseDown = () => {
    const pos = stage.getPointerPosition();
    startPos = pos;

    ellipse = new konva.Ellipse({
      id: generateRandomId(),
      x: pos.x,
      y: pos.y,
      radiusX: 0,
      radiusY: 0,
      stroke: ELLIPSE_ELEMENT_CONFIG.DRAW_MODE_STROKE_COLOR,
      strokeWidth: getCanvasRelativeSize(
        ELLIPSE_ELEMENT_CONFIG.DRAW_MODE_STROKE_WIDTH
      ),
    });
    layer.add(ellipse);
  };

  /**
   * updating radius on moving mouse
   */
  const onMouseMove = () => {
    if (!startPos) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const radiusX = Math.abs((pos.x - startPos.x) / 2);
    const radiusY = Math.abs((pos.y - startPos.y) / 2);
    ellipse.radiusX(isShiftPressed ? _.min([radiusX, radiusY]) : radiusX);
    ellipse.radiusY(isShiftPressed ? _.min([radiusX, radiusY]) : radiusY);
    ellipse.x((pos.x + startPos.x) / 2);
    ellipse.y((pos.y + startPos.y) / 2);

    layer.batchDraw();
  };

  /**
   * on mouse up, adding ellipse in canvas and sending callback
   */
  const onMouseUp = () => {
    if (!ellipse || !startPos) return;

    const payload = {
      radiusX: Math.max(ellipse.radiusX(), 20),
      radiusY: Math.max(ellipse.radiusY(), 20),
      x: ellipse.x(),
      y: ellipse.y(),
    };

    if (_.isFunction(props?.onDrawComplete)) props.onDrawComplete(payload);

    ellipse = null;
    startPos = null;
    stage.container().style.cursor = "default";
    stage.setAttr(KONVA_ATTR.IS_DRAWING_POLYGON, false);

    if (_.isFunction(cleanClickEvent)) cleanClickEvent();
    if (_.isFunction(cleanKeyUpEvent)) cleanKeyUpEvent();
    if (_.isFunction(cleanKeyDownEvent)) cleanKeyDownEvent();
  };

  listenBackEvents(stage, transformer, {
    onMouseDown,
    onMouseMove,
    onMouseUp,
  });
};

/**
 *
 * @param {*} node
 * @param {*} konva
 * @returns is ellipse node or not
 */
export const isEllipseNode = (node, konva) => {
  return node && node instanceof konva.Ellipse;
};
